'use client';
import { useEffect, useRef, useState } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { useAuth } from '@/hooks/useAuth';
import { signOut } from '@/lib/auth';
import { changePassword, getPasswordCompliance, ENTERPRISE_PASSWORD_POLICY, ChangePasswordError } from '@/lib/api';
import { checkPasswordPolicy } from '@/lib/passwordPolicy';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
/**
 * Blocking gate for Enterprise-scoped users whose current password predates (or
 * doesn't meet) the Enterprise password policy. Backed by GET /auth/password-compliance.
 * When a reset is required, the app is covered by a mandatory password-change
 * screen until the user sets a compliant password (or signs out).
 */ export function EnterprisePasswordResetGate() {
    const t = useTranslations$1();
    const { isAnonymous, isLoading: authLoading } = useAuth();
    const [resetRequired, setResetRequired] = useState(false);
    // The Enterprise policy is fixed (14 chars + all four character classes) and the
    // gate only ever shows for Enterprise-scoped users, so use the constant directly
    // rather than fetching — a fetched fallback to STANDARD would under-validate and
    // enable submit for passwords the backend rejects.
    const policy = ENTERPRISE_PASSWORD_POLICY;
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Monotonic guard so a slow in-flight compliance check can't override a newer
    // result — including a successful submit that already cleared the gate.
    const checkSeqRef = useRef(0);
    useEffect(()=>{
        if (authLoading || isAnonymous) return;
        let cancelled = false;
        // Re-check on mount and whenever the window regains focus, so the gate clears
        // when the requirement is lifted (or the user fixed their password elsewhere)
        // and appears if it newly applies mid-session — without a full page refresh.
        const check = async ()=>{
            const seq = ++checkSeqRef.current;
            // .catch makes the fail-open behaviour explicit (no unhandled rejection on
            // a network error) — a transient failure must not trap the user.
            const { resetRequired: required } = await getPasswordCompliance().catch(()=>({
                    resetRequired: false
                }));
            if (cancelled || seq !== checkSeqRef.current) return;
            setResetRequired(required);
        };
        void check();
        const onFocus = ()=>void check();
        window.addEventListener('focus', onFocus);
        return ()=>{
            cancelled = true;
            window.removeEventListener('focus', onFocus);
        };
    }, [
        authLoading,
        isAnonymous
    ]);
    // Never show the overlay for a signed-out/anonymous session, even if a late
    // compliance response set resetRequired just before auth was lost.
    if (authLoading || isAnonymous || !resetRequired) return null;
    const passwordCheck = checkPasswordPolicy(newPassword, policy);
    const passwordsMatch = newPassword === confirmPassword;
    const canSubmit = passwordCheck.ok && passwordsMatch && !isSaving;
    const handleSubmit = async (e: React.FormEvent): Promise<void> =>{
        e.preventDefault();
        setError(null);
        if (!passwordCheck.ok || !passwordsMatch) return;
        try {
            setIsSaving(true);
            // changePassword enforces the Enterprise policy server-side, so a success
            // means the new password is compliant — dismiss without re-fetching. The
            // best-effort compliance stamp is independent: if it failed, the gate simply
            // reappears on a later load rather than trapping the user behind a misleading
            // error now.
            await changePassword(newPassword);
            // Invalidate any in-flight compliance check so a stale response can't
            // reopen the gate after we clear it.
            checkSeqRef.current++;
            setResetRequired(false);
        } catch (err) {
            const message = err instanceof ChangePasswordError ? err.message : err instanceof Error ? err.message : null;
            setError(message || t("3yhioM"));
        } finally{
            setIsSaving(false);
        }
    };
    return (<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md space-y-5 rounded-lg border border-border bg-card p-6 shadow-xl">
        <div className="space-y-1.5">
          <h2 className="text-lg font-semibold text-foreground">{t("_fxrW7")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("5ziheF")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="enterprise-new-password" className="text-sm font-medium">
              {t("fTHhSB")}
            </Label>
            <Input id="enterprise-new-password" type="password" autoFocus autoComplete="new-password" value={newPassword} onChange={(e)=>{
        setNewPassword(e.target.value);
        setError(null);
    }} minLength={policy.minLength} disabled={isSaving}/>
          </div>

          {newPassword !== '' && <PasswordRequirements password={newPassword} policy={policy}/>}

          <div className="space-y-2">
            <Label htmlFor="enterprise-confirm-password" className="text-sm font-medium">
              {t("8HimVK")}
            </Label>
            <Input id="enterprise-confirm-password" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e)=>{
        setConfirmPassword(e.target.value);
        setError(null);
    }} disabled={isSaving}/>
          </div>

          {confirmPassword !== '' && !passwordsMatch && (<p className="text-xs text-destructive">{t("7Chrsf")}</p>)}
          {error && <p className="text-xs text-destructive">{error}</p>}

          <div className="flex items-center justify-between gap-3 pt-1">
            <button type="button" onClick={()=>void signOut().then(()=>(window.location.href = '/'))} className="text-xs text-muted-foreground underline-offset-2 hover:underline">
              {t("xXbJso")}
            </button>
            <Button type="submit" disabled={!canSubmit}>
              {isSaving ? t("K0sz9Z") : t("KkT_Fh")}
            </Button>
          </div>
        </form>
      </div>
    </div>);
}
